import { getMembershipPlanDisplayPricing } from '@frontend/api-client';
import { formatCurrencyNatural, match } from '@frontend/duck-tape';
import { getPlanSubtitle, usePaywallContext } from '@frontend/react-domain';
import { Button, Text, XStack, YStack } from '@frontend/web-react';
import { CalloutBadge } from '@frontend/web-react/components/Primitives/Badges';
import { usePricingTerminology } from '@frontend/web/context';
import { usePaywallPricingContext } from '../../PaywallPricingContext';
import { PaywallSelectionItem } from '../../PaywallSelectionItem';
import { SkeletonPlanCard } from '../../SkeletonPlanCard';

export const SubscriptionPlanSelector = () => {
  const { paywallActions, paywallState } = usePaywallContext();
  const { isError, isLoading, pricingState, refetch, selectablePlans } = usePaywallPricingContext();
  const { selectedPlanType, selectedPurchaseType } = paywallState.purchase;
  const { onSelectPlan } = paywallActions.purchase;
  const { pricingTerminology, showCredits } = usePricingTerminology();

  if (isLoading) {
    return (
      <YStack accessibilityLabel="Loading subscription plans" className="h-full" gapY="md">
        <YStack bg="lineGray" height={24} radius="md" width="60%" />
        <YStack gapY="md">
          <SkeletonPlanCard />
          <SkeletonPlanCard />
          <SkeletonPlanCard />
        </YStack>
      </YStack>
    );
  }

  if (isError) {
    return (
      <YStack accessibilityLabel="Error loading subscription plans" className="h-full" gapY="md">
        <Text type="h4">Your plans took a duck dive.</Text>
        <Text color="secondary" type="p-sm">
          {"Don't worry, we'll bring them back. Try again."}
        </Text>
        <Button label="Try again" onClick={() => refetch()} size="sm" />
      </YStack>
    );
  }

  if (selectablePlans.length === 0) {
    return (
      <YStack accessibilityLabel="No subscription plans available" className="h-full" gapY="md">
        <Text type="h4">No plans available</Text>
        <Text color="secondary" type="p-sm">
          There are no membership plans available at this time.
        </Text>
      </YStack>
    );
  }

  return (
    <YStack accessibilityLabel="Subscription plan selector" className="h-full" gapY="md">
      <YStack gapY="xs">
        <Text type="h3">
          {match(selectedPurchaseType)
            .with('Subscription', () =>
              paywallState.promotionTranslation.promotionDescription
                ? 'Pick a membership plan for later'
                : 'Pick your membership plan',
            )
            .with('ALaCarteCredits', () =>
              showCredits ? `Pick your ${pricingTerminology.unit_term_plural} pack` : 'Pick your package',
            )
            .otherwise(() => '')}
        </Text>

        {paywallState.promotionTranslation.promotionDescription ? (
          <Text type="p-sm">
            {"You'll"} renew on this plan after your promotion ends (you can always update it later).
          </Text>
        ) : null}
      </YStack>

      <YStack flex={1} gapY="md">
        {selectablePlans.map((plan) => {
          const isSelected = selectedPlanType === plan.planType;
          const planPricing = getMembershipPlanDisplayPricing(plan);
          return (
            <PaywallSelectionItem
              isSelected={isSelected}
              key={plan.planType}
              leftContent={
                <YStack flex={1} gapY="xs">
                  <XStack alignItems="center" gapX="md">
                    <Text type="h4">{plan.label}</Text>
                    {plan.planCode === 'HOUSEHOLD' ? <CalloutBadge>MOST POPULAR</CalloutBadge> : null}
                  </XStack>
                  <YStack gapY="sm">
                    <XStack alignItems="center" gapX="xs">
                      {pricingState === 'transitioning' ? (
                        <YStack gapY="xs">
                          <YStack bg="lineGray" height={24} radius="md" width={88} />
                          <YStack bg="lineGray" height={16} radius="md" width={64} />
                        </YStack>
                      ) : (
                        <>
                          <Text className="leading-6" type="h2">
                            {formatCurrencyNatural(planPricing?.primaryPrice ?? plan.price)}
                          </Text>
                          <Text className="leading-4" type="p-md">
                            /mo
                          </Text>
                        </>
                      )}
                    </XStack>
                    {pricingState !== 'transitioning' && planPricing?.secondaryPrice ? (
                      <Text className="line-through" color="tertiary" type="p-sm">
                        {formatCurrencyNatural(planPricing.secondaryPrice)}/mo
                      </Text>
                    ) : null}
                  </YStack>
                </YStack>
              }
              onClick={() => onSelectPlan(plan.planType)}
              rightContent={
                <YStack className="text-right">
                  <Text color="primary" textAlign="end" type="p-sm">
                    {getPlanSubtitle(plan, pricingTerminology)}
                  </Text>
                  {showCredits ? (
                    <Text color="primary" textAlign="end" type="p-sm">
                      ~ {plan.estimatedTasksPerMonth.min} - {plan.estimatedTasksPerMonth.max} tasks
                    </Text>
                  ) : null}
                </YStack>
              }
            />
          );
        })}

        <YStack p="sm">
          <Text className="italic" type="caption">
            All plans include expert copilots to support you every step of the way; smart automation that anticipates
            and streamlines your tasks; exclusive, member-only perks and offers; and seamless syncing for effortless
            task management across devices.
          </Text>
        </YStack>
      </YStack>
    </YStack>
  );
};
